/* eslint-disable max-len */
import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Card,
  CardContent,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Icon,
  Tooltip,
  Typography,
  LinearProgress,
  Box,
} from '@mui/material';
import {
  CsvUtil,
  DataUtil,
  StoreUtil,
  PatientType,
  PatientTypes,
  ClaimQueryType,
  LocationTypeUtil,
  SpecialtyUtil
} from 'doctivity-shared/utils';
import {
  ClaimsDateProvider,
  TitleTooltip,
  LoadingView,
  ServiceLineSelect,
  MarketOrTagSelect,
  Paginateable,
  ProviderProjectedPatientsChart,
  OrgProjectedPatientsChart,
  TaxonomySelect,
  PDFExportDialog
} from 'components';
import { MarketsUtil, ServiceLineUtil, TagsUtil, ColorUtil } from 'utils';
import { loadAnalytics } from 'store/actions/analyticsActions';
import { setProviderType } from 'store/actions/appActions';
import axiosInstance from 'utils/axiosUtil';

import fileDownload from 'js-file-download';
import moment from 'moment';
import { latestClaimsDate } from '../../../config/claims-date';
import { listMarkets } from 'store/actions/marketsActions';
import { listClaimsServicelines } from 'store/actions/claimsActions';
import { setPatientType } from 'store/actions/appActions';
import { withReportCriteria } from 'market-data/useReportCriteria';
import { queryTaxonomyCodes } from 'store/actions/taxonomyActions';

const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2),
    },
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: theme.spacing(2),
      marginBottom: theme.spacing(1),
      '& .MuiFormControl-root': {
        width: '100%',
        minWidth: 'unset',
      },
      '& .MuiOutlinedInput-root': {
        height: 44,
      },
      '& .MuiInputLabel-root': {
        fontSize: 14,
      },
      '& .MuiSelect-select': {
        fontSize: 14,
      },
      '& .MuiAutocomplete-inputRoot': {
        height: 44,
        paddingTop: '0 !important',
        paddingBottom: '0 !important',
      },
    },
  },
  filterDropDown: {
    minWidth: 168,
    marginRight: theme.spacing(),
    [theme.breakpoints.down('md')]: {
      width: '100%',
      minWidth: 'unset',
      marginRight: 0,
      '& .MuiOutlinedInput-root': {
        height: 44,
      },
      '& .MuiInputLabel-root': {
        fontSize: 14,
      },
      '& .MuiSelect-select': {
        fontSize: 14,
        padding: '10px 14px',
      },
    },
  },
  colorPagination: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(1),
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
    },
  },
  dropdownsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    '& > *': {
      marginBottom: theme.spacing(1),
    },
    [theme.breakpoints.down('md')]: {
      display: 'contents',
      '& > *': {
        marginBottom: 0,
      },
    },
  },
  pagination: {
    marginLeft: 'auto',
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
      width: '100%',
    },
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'end',
  },
  headerDropDown: {
    minWidth: 220,
    marginBottom: theme.spacing(2),
  },
  providers: {
    width: '100%',
    paddingTop: 20,
    paddingBottom: 20,
    overflow: 'auto',
  },
  providerRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    height: 24,
    marginBottom: 4,
    color: '#2E93fA',
    fontSize: 12,
    fontWeight: 300,
    position: 'relative',
    width: '100%',
    whiteSpace: 'nowrap',
  },
  providerBar: {
    position: 'absolute',
    backgroundColor: '#2E93fA',
    transitionProperty: 'width',
    transitionDelay: '1s',
    transitionDuration: '1s',
    width: 0,
    height: 24,
    top: 0,
    left: 0,
    borderRadius: 2,
    zIndex: 1,
    overflow: 'hidden',
    color: '#ffffff',
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    gap: theme.spacing(1),
  },
  providerName: {
    display: 'flex',
    flexDirection: 'row',
    paddingTop: 2,
    gap: 8,
    zIndex: 0,
  },
  providerNameIcon: {
    fontSize: 10,
    paddingLeft: 6,
  },
  providerCount: {
    position: 'absolute',
    top: 7,
    left: 200,
    fontSize: 11,
    fontWeight: 300,
    zIndex: 0,
  },
  providerNameBar: {
    paddingTop: 6,
    zIndex: 2,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
  },
  providerCountBar: {
    position: 'absolute',
    top: 7,
    left: 200,
    fontSize: 11,
    fontWeight: 300,
    zIndex: 3,
    overflow: 'hidden',
  },
  organizationCount: {
    position: 'absolute',
    top: 7,
    left: 440,
    fontSize: 11,
    fontWeight: 300,
    zIndex: 0,
  },
  organizationCountBar: {
    position: 'absolute',
    top: 7,
    left: 440,
    fontSize: 11,
    fontWeight: 300,
    zIndex: 3,
    overflow: 'hidden',
  },
  leftIcon: {
    marginRight: theme.spacing(1),
  },
  rightIcon: {
    marginLeft: theme.spacing(1),
  },
  spacer: {
    flex: 1,
  },
  infoTipIcon: {
    position: 'relative',
    top: -6,
    fontSize: 14,
    fontWeight: 200,
    color: '#999999',
  },
  infoTip: {
    fontSize: 13,
    fontWeight: 200,
  },
  affiliatedContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(1),
    },
  },
  affiliatedHeader: {
    display: 'flex',
    flexDirection: 'row',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'row',
      flexWrap: 'nowrap',
    },
  },
  affiliated: {
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 2,
    backgroundColor: ColorUtil.AffiliatedColor,
    color: '#FFFFFF',
    padding: '4px 12px',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      fontSize: 11,
      padding: '3px 8px',
    },
  },
  nonAffiliated: {
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 2,
    backgroundColor: ColorUtil.NonAffiliatedColor,
    color: '#FFFFFF',
    padding: '4px 12px',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      fontSize: 11,
      padding: '3px 8px',
    },
  },
  affiliatedSpacer: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      marginRight: theme.spacing(1),
      marginBottom: 0,
    },
  },
  affiliatedPercentage: {
    fontSize: 14,
    fontWeight: 600,
    marginRight: theme.spacing(0.5),
    [theme.breakpoints.down('md')]: {
      fontSize: 12,
    },
  },
  affiliatedIcon: {
    fontSize: 14,
    fontWeight: 400,
    marginRight: theme.spacing(1),
    color: '#FFFFFF',
    [theme.breakpoints.down('md')]: {
      fontSize: 12,
      marginRight: theme.spacing(0.5),
    },
  },
  affiliatedInfoTipIcon: {
    position: 'relative',
    top: -4,
    marginLeft: theme.spacing(),
    fontSize: 14,
    fontWeight: 200,
    color: '#999999',
  },
  affiliatedInfoTip: {
    fontSize: 13,
    fontWeight: 200,
  },
  aggregatePercentage: {
    marginLeft: theme.spacing(1),
  },
  colorKeyContainer: {
    minHeight: 40,
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      minHeight: 'auto',
      marginBottom: theme.spacing(1),
    },
  },
  colorKey: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'nowrap',
    fontSize: 12,
    fontWeight: 400,
    [theme.breakpoints.down('md')]: {
      flexWrap: 'nowrap',
      fontSize: 11,
    },
  },
  colorKeyIcon: {
    width: 14,
    height: 14,
    marginRight: 2,
    borderRadius: 2,
    [theme.breakpoints.down('md')]: {
      width: 12,
      height: 12,
    },
  },
  colorKeyText: {
    marginRight: 12,
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('md')]: {
      marginRight: 8,
    },
  },
  disclaimerContent: {
    color: '#9E9E9E',
    fontStyle: 'italic',
    fontSize: 13,
    textAlign: 'center',
  },
  progressContainer: {
    width: '100%',
    maxWidth: 400,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginTop: 8,
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  progressPhase: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.primary.main,
    marginBottom: 4,
  },
});

class ClaimsPage extends React.Component {
  findServiceLine = () => {};

  // Number of seconds to wait before saving the currently selected criteria
  saveCriteriaInterval = 3;

  saveCriteriaTimer = null;
  fetchDataTimer = null;
  
  progressPollInterval = null;

  constructor(props) {
    super(props);

    this.state = {
      query: {
        offset: 0,
        limit: 200,
      },
      specialties: [],
      customServiceLineProgress: null,
      showPdfExport: false,
    };
  }




  componentDidMount() {

    const { dispatch } = this.props;

    if (
      !this.props.markets ||
      StoreUtil.needsLoadLongCache(this.props.markets)
    ) {
      dispatch(listMarkets(this.props.clientId));
    }

    if (!StoreUtil.isLoaded(this.props.taxonomyCodes)) {
      this.fetchSpecialties();
    }
    this.fetchCustomServiceLineProgress();
    this.startProgressPolling();
  }

  componentWillUnmount() {
    this.stopProgressPolling();
    clearTimeout(this.fetchDataTimer);
  }

  startProgressPolling = () => {
    // Clear any existing interval
    this.stopProgressPolling();
    
    // Only start polling if there's a processing service line
    if (this.getProcessingCustomServiceLine()) {
      this.progressPollInterval = setInterval(() => {
        this.fetchCustomServiceLineProgress();
      }, 5000); // Poll every 5 seconds
    }
  };

  stopProgressPolling = () => {
    if (this.progressPollInterval) {
      clearInterval(this.progressPollInterval);
      this.progressPollInterval = null;
    }
  };


  checkCustomServiceLineProcessing = () => {
    const { servicelines, clientId } = this.props;
    const { customServiceLineProgress } = this.state;
    const servicelineIds = this.getServicelineIds();
    
    if (!servicelineIds || !servicelines) {
      return false;
    }
    
    const ids = Array.isArray(servicelineIds) ? servicelineIds : [servicelineIds];
    
    // Find custom service lines (they have client_id matching current client)
    for (const sl of servicelines) {
      if (sl.client_id === clientId && ids.includes(sl.id)) {
        // Check if this serviceline appears to be processing in redux
        if (sl.start_processing_date && !sl.end_processing_date) {
          // But check progress API state - it may know it's actually complete
          // Only check if progress data is for THIS serviceline
          if (customServiceLineProgress) {
            // If progress shows complete, trust the API over stale redux data
            if (customServiceLineProgress.status === 'done') {
              return false;
            }
          }
          return true;
        }
      }
    }
    
    return false;
  };

  getProcessingCustomServiceLine = () => {
    const { servicelines, clientId } = this.props;
    const servicelineIds = this.getServicelineIds();
    
    if (!servicelineIds || !servicelines) {
      return null;
    }
    
    const ids = Array.isArray(servicelineIds) ? servicelineIds : [servicelineIds];
    
    for (const sl of servicelines) {
      if (sl.client_id === clientId && ids.includes(sl.id)) {
        if (sl.start_processing_date && !sl.end_processing_date) {
          return sl;
        }
      }
    }
    
    return null;
  };

  fetchCustomServiceLineProgress = async () => {
    const sl = this.getProcessingCustomServiceLine();
    
    if (!sl) {
      this.setState({ customServiceLineProgress: null });
      this.stopProgressPolling();
      return;
    }
    
    try {
      const response = await axiosInstance.get('/ClaimsServicelines', {
        params: { progress: sl.id }
      });
      const progress = response.data[sl.id] || null;
      
      // Check if processing just completed
      if (progress && progress.status === 'done') {
        this.setState({ customServiceLineProgress: progress });
        this.stopProgressPolling();
        
        // Refresh servicelines list to get updated end_processing_date
        this.props.dispatch(listClaimsServicelines());
        
        // Fetch the chart data
        this.fetchData();
      } else {
        this.setState({ customServiceLineProgress: progress });
      }
    } catch (err) {
      console.error('Error fetching custom service line progress:', err);
      this.setState({ customServiceLineProgress: null });
    }
  };

  renderCustomServiceLineProgress = () => {
    const { classes } = this.props;
    const { customServiceLineProgress: p } = this.state;
    const sl = this.getProcessingCustomServiceLine();
    
    if (!sl) {
      return null;
    }
    
    // If progress shows complete, don't render progress bar
    // (servicelines list will refresh and show the data)
    if (p && p.status === 'done') {
      return null;
    }
    
    let percentage = 0;
    let currentPhase = 'Processing...';
    
    if (p) {
      if (p.currentEntityType === 'prov') {
        const provTotal = p.providersTotal || 0;
        const provCount = p.providersCount || 0;
        if (provTotal > 0) {
          // Providers are 0-50% of overall progress
          percentage = Math.round((provCount / provTotal) * 50);
        }
        currentPhase = `Providers: ${provCount.toLocaleString()}${provTotal > 0 ? ` / ${provTotal.toLocaleString()}` : ''} processed`;
      } else if (p.currentEntityType === 'org') {
        const orgTotal = p.organizationsTotal || 0;
        const orgCount = p.organizationsCount || 0;
        if (orgTotal > 0) {
          // Organizations are 50-100% of overall progress
          percentage = 50 + Math.round((orgCount / orgTotal) * 50);
        } else {
          percentage = 50; // If no orgs to process, we're at 50%
        }
        currentPhase = `Organizations: ${orgCount.toLocaleString()}${orgTotal > 0 ? ` / ${orgTotal.toLocaleString()}` : ''} processed`;
      }
    }
    
    // If we have totals, show determinate progress; otherwise indeterminate
    const hasTotals = p && ((p.providersTotal || 0) > 0 || (p.organizationsTotal || 0) > 0);
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <Typography className={classes.progressPhase}>
          Processing Custom Service Line: {sl.name}
        </Typography>
        <Box className={classes.progressContainer}>
          <div className={classes.progressLabel}>
            <span>{currentPhase}</span>
            {hasTotals && <span>{percentage}%</span>}
          </div>
          <LinearProgress
            variant={hasTotals ? "determinate" : "indeterminate"}
            value={percentage}
            className={classes.progressBar}
          />
        </Box>
        <Typography className={classes.disclaimerContent} style={{ marginTop: 16 }}>
          Progress updates automatically every few seconds.
        </Typography>
      </div>
    );
  };

  fetchSpecialties() {
    const { dispatch } = this.props;

    let queryCodes = {
      order: [['code', 'ASC']],
      limit: 10000,
      offset: 0,
    }

    let qryCodes = queryTaxonomyCodes({
      ...queryCodes,
    });

    dispatch(
      qryCodes
    );
  }

  marketsChanged(newMarkets, oldMarkets) {
    if (
      newMarkets === null ||
      (oldMarkets === null && newMarkets !== oldMarkets)
    ) {
      return true;
    }
    if (newMarkets && oldMarkets && newMarkets.data && oldMarkets.data) {
      if (newMarkets.data.length !== oldMarkets.data.length) {
        return true;
      }
      if (
        newMarkets.meta &&
        newMarkets.meta.statusTime &&
        oldMarkets.meta &&
        oldMarkets.meta.statusTime
      ) {
        return (
          newMarkets.meta.statusTime.statusSuccess !==
          oldMarkets.meta.statusTime.statusSuccess
        );
      }
    }
    return false;
  }

  componentDidUpdate(prevProps) {
    const { router } = this.props;

    if (this.props.clientId !== prevProps.clientId) {
      router.navigate('/claims');
      return;
    }

    if (
      this.marketsChanged(this.props.markets, prevProps.markets)
    ) {
      this.debouncedFetchData();
    }
    if (JSON.stringify(this.props.currentCriteria) !== JSON.stringify(prevProps.currentCriteria)) {
      // Clear progress state first when serviceline changes to avoid stale data
      if (this.props.currentCriteria?.serviceline !== prevProps.currentCriteria?.serviceline) {
        this.setState({ customServiceLineProgress: null }, () => {
          this.debouncedFetchData();
          this.fetchCustomServiceLineProgress();
          this.startProgressPolling();
        });
      } else {
        this.debouncedFetchData();
        this.fetchCustomServiceLineProgress();
      }
    }
    // Check if servicelines changed (e.g., processing started/completed)
    if (this.props.servicelines !== prevProps.servicelines) {
      this.fetchCustomServiceLineProgress();
      this.startProgressPolling();
    }
    //if (this.props.currentCriteria?.ready != prevProps.currentCriteria?.ready && this.props.currentCriteria?.ready) {
      //this.props.dispatch(setPatientType(this.props.currentCriteria.patientType));
      //this.props.dispatch(setProviderType(this.props.currentCriteria.providerType));
    //}
  }

  debouncedFetchData(delay = 300) {
    clearTimeout(this.fetchDataTimer);
    this.fetchDataTimer = setTimeout(() => this.fetchData(), delay);
  }

  async fetchData(csv) {
    const servicelineIds = this.getServicelineIds();
    const { claimsType } = this.props.currentCriteria;
    const type =
      claimsType === 'providers'
        ? ClaimQueryType.CLAIMS_PER_PROVIDER
        : ClaimQueryType.CLAIMS_PER_ORGANIZATION;
    const { currentCriteria } = this.props;
    const { patientType, ready, market, isMarket, tag, claimSetting, taxonomies } =
      currentCriteria;
    if (!ready) return;

    const options = {
      type,
      filter: {
        serviceline:
          !servicelineIds || servicelineIds.length === 0
            ? ServiceLineUtil.getDefault()
            : servicelineIds,
        codes: this.getCodes(),
        codes_type: this.getCodesType(),
        market: isMarket ? { id: market } : undefined,
        tag: !isMarket ? { id: tag } : undefined,
        location_type: claimSetting === 0 ? undefined : claimSetting,
        patient_type: patientType,
        taxonomies: claimsType === 'providers' ? taxonomies : undefined,
        affiliated_client_id: this.props.clientId,
      },
      opts: this.state.query,
    };
    if (!options.filter.market && !options.filter.tag) {
      return;
    }
    if (!csv) {
      this.props.dispatch(
        loadAnalytics({
          ...options,
        }),
      );
      if (type === ClaimQueryType.CLAIMS_PER_ORGANIZATION) {
        this.props.dispatch(
          loadAnalytics({
            ...options,
            type: ClaimQueryType.CLAIMS_PER_ORGANIZATION_COUNTS,
          }),
        );
      } else if (type === ClaimQueryType.CLAIMS_PER_PROVIDER) {
        this.props.dispatch(
          loadAnalytics({
            ...options,
            type: ClaimQueryType.CLAIMS_PER_PROVIDER_COUNTS,
          }),
        );
      }
    } else {
      axiosInstance
        .post(
          '/Analytics',
          {
            ...options,
            format: 'csv',
            headers: {
              'Content-Type': 'text/csv',
            },
          },
          {
            params: {
              selected_client_id: this.props.clientId,
            },
          },
        )
        .then((response) => {
          let type = '';
          switch (this.props.currentCriteria?.patientType) {
            case PatientType.ALL_PATIENTS:
              type = options.type.replace('CLAIMS', 'PROJECTED_PATIENTS');
              break;
            case PatientType.CAPTURED_CLAIMS:
              type = options.type;
              break;
            case PatientType.PEDIATRIC_PATIENTS:
              type = options.type.replace(
                'CLAIMS',
                'CAPTURED_PEDIATRIC_PATIENTS',
              );
              break;
            default:
              break;
          }
          let firstServiceLineId = Array.isArray(servicelineIds)
            ? servicelineIds[0]
            : servicelineIds;
          if (!firstServiceLineId) {
            firstServiceLineId = ServiceLineUtil.getDefault();
          }
          const firstServiceLine = ServiceLineUtil.findServiceLine(
            this.props.servicelines,
            firstServiceLineId,
          );
          const servicelineName =
            firstServiceLine && firstServiceLine.name
              ? `_${firstServiceLine.name}`
              : '';
          const filename = `doctivity_${type}${servicelineName}_${moment().format('YY_MM_DD')}.csv`;
          try {
            const data = this.formatCSV(response.data, type);
            fileDownload(data, filename);
          } catch (err) {
            console.error(`Could not format csv for ${filename}`);
            console.error(err);
            fileDownload(response.data, filename);
          }
        });
    }
  }

  formatCSV(data, type) {
    let columns = CsvUtil.stringToMatrix(data);
    const urlPrefix = `${process.env.WEB_DOMAIN}/${
      type.includes('ORGANIZATION')
        ? 'organizations'
        : 'providers'
    }`;

    const isProvider = type.includes('PROVIDER');

    if (columns.length > 0) {
      let locationTypeIndex = -1;
      let affiliatedIndex = -1;
      let idIndex = -1;
      let taxonomyIndices = [];
      let primaryTaxonomyIndex = -1;
      let columnsToRemove = [];

      for (let index = 0; index < columns[0].length; index++) {
        if (columns[0][index] === 'location_type') {
          locationTypeIndex = index;
          columns[0][index] = 'setting';
        } else if (
          columns[0][index] === 'organization.is_affiliated.client_id' ||
          columns[0][index] === 'provider.is_affiliated.client_id'
        ) {
          columns[0][index] = 'Affiliated';
          affiliatedIndex = index;
          // REMOVED: idIndex = index+1; // This was incorrectly overwriting idIndex
        } else if (
          columns[0][index] === 'organization.id' ||
          columns[0][index] === 'provider.id'
        ) {
          idIndex = index;
        } else if (isProvider && columns[0][index].startsWith('provider.taxonomy.')) {
          taxonomyIndices.push(index);
        } else if (isProvider && columns[0][index] === 'provider.primary_taxonomy') {
          primaryTaxonomyIndex = index;
        } else if (isProvider && columns[0][index] === 'provider.location.organization.name') {
          // Rename to Organization Name
          columns[0][index] = 'Organization Name';
        } else if (isProvider && columns[0][index] === 'provider.location.name') {
          // Rename to Location Name
          columns[0][index] = 'Location Name';
        } else if (isProvider && columns[0][index] === 'provider.location.address1') {
          columns[0][index] = 'Address';
        } else if (isProvider && columns[0][index] === 'provider.location.city') {
          columns[0][index] = 'City';
        } else if (isProvider && columns[0][index] === 'provider.location.state') {
          columns[0][index] = 'State';
        } else if (isProvider && columns[0][index] === 'provider.location.postal_code') {
          columns[0][index] = 'Postal Code';
        } else if (isProvider && (
          columns[0][index] === 'provider.location.organization.id' ||
          columns[0][index] === 'provider.location.organization' ||
          columns[0][index] === 'provider.location.id' ||
          columns[0][index] === 'provider.location.organization_id' ||
          columns[0][index] === 'provider.is_affiliated'
        )) {
          // Remove these extra columns
          columnsToRemove.push(index);
        }
      }

      // For providers, add Specialty column before removing taxonomy columns
      if (isProvider && primaryTaxonomyIndex > -1) {
        // Insert Specialty column header after primary_taxonomy
        columns[0].splice(primaryTaxonomyIndex + 1, 0, 'Specialty');
        
        // Calculate specialty for each row
        for (let rowIndex = 1; rowIndex < columns.length - 1; rowIndex++) {
          const primaryTaxonomy = columns[rowIndex][primaryTaxonomyIndex];
          
          // Create taxonomy object from the row data
          const taxonomy = {
            code: primaryTaxonomy,
          };
          
          // Find taxonomy detail columns if they exist
          taxonomyIndices.forEach(colIndex => {
            const headerName = columns[0][colIndex];
            if (headerName === 'provider.taxonomy.code') {
              taxonomy.code = columns[rowIndex][colIndex];
            } else if (headerName === 'provider.taxonomy.classification') {
              taxonomy.classification = columns[rowIndex][colIndex];
            } else if (headerName === 'provider.taxonomy.specialization') {
              taxonomy.specialization = columns[rowIndex][colIndex];
            } else if (headerName === 'provider.taxonomy.doctivity_name') {
              taxonomy.doctivity_name = columns[rowIndex][colIndex];
            }
          });
          
          // Get specialty name using SpecialtyUtil
          const specialtyName = SpecialtyUtil.getNameFromTaxonomy(taxonomy);
          
          // Insert specialty value after primary_taxonomy (accounting for header insert)
          columns[rowIndex].splice(primaryTaxonomyIndex + 1, 0, specialtyName || '');
        }
        
        // Adjust indices after insertion
        taxonomyIndices = taxonomyIndices.map(idx => idx > primaryTaxonomyIndex ? idx + 1 : idx);
        columnsToRemove = columnsToRemove.map(idx => idx > primaryTaxonomyIndex ? idx + 1 : idx);
        if (affiliatedIndex > primaryTaxonomyIndex) affiliatedIndex++;
        if (idIndex > primaryTaxonomyIndex) idIndex++;
        if (locationTypeIndex > primaryTaxonomyIndex) locationTypeIndex++;
      }

      // Remove taxonomy columns (in reverse order to maintain indices)
      if (isProvider && taxonomyIndices.length > 0) {
        taxonomyIndices.sort((a, b) => b - a);
        taxonomyIndices.forEach(colIndex => {
          columns = DataUtil.removeMatrixColumn(columns, colIndex);
          // Adjust other indices
          if (affiliatedIndex > colIndex) affiliatedIndex--;
          if (idIndex > colIndex) idIndex--;
          if (locationTypeIndex > colIndex) locationTypeIndex--;
          if (primaryTaxonomyIndex > colIndex) primaryTaxonomyIndex--;
          columnsToRemove = columnsToRemove.map(idx => idx > colIndex ? idx - 1 : idx);
        });
      }

      // Remove primary_taxonomy column for providers
      if (isProvider && primaryTaxonomyIndex > -1) {
        columns = DataUtil.removeMatrixColumn(columns, primaryTaxonomyIndex);
        if (affiliatedIndex > primaryTaxonomyIndex) affiliatedIndex--;
        if (idIndex > primaryTaxonomyIndex) idIndex--;
        if (locationTypeIndex > primaryTaxonomyIndex) locationTypeIndex--;
        columnsToRemove = columnsToRemove.map(idx => idx > primaryTaxonomyIndex ? idx - 1 : idx);
      }

      // Remove extra columns (in reverse order)
      if (columnsToRemove.length > 0) {
        columnsToRemove.sort((a, b) => b - a);
        columnsToRemove.forEach(colIndex => {
          columns = DataUtil.removeMatrixColumn(columns, colIndex);
          if (affiliatedIndex > colIndex) affiliatedIndex--;
          if (idIndex > colIndex) idIndex--;
          if (locationTypeIndex > colIndex) locationTypeIndex--;
        });
      }

      if (affiliatedIndex > -1) {
        for (let index = 1; index < columns.length - 1; index++) {
          columns[index][affiliatedIndex] = columns[index][affiliatedIndex]
            ? 'Yes'
            : 'No';
        }
      } else {
        columns[0].push('Affiliated');
        for (let index = 1; index < columns.length - 1; index++) {
          columns[index].push('No');
        }
      }

      if (locationTypeIndex > -1) {
        columns[0].push('URL');
        // eslint-disable-next-line no-restricted-syntax
        for (let index = 1; index < columns.length - 1; index++) {
          const locationType = parseInt(columns[index][locationTypeIndex], 10);
          const locationTypeName = LocationTypeUtil.getName(locationType);
          columns[index][locationTypeIndex] = locationTypeName;
          if (idIndex > -1) {
            columns[index].push(
              `${urlPrefix}/${columns[index][0]}/market`,
            );
          }
        }
      }

      // Remove provider.id / organization.id column
      if (idIndex > -1) {
        columns = DataUtil.removeMatrixColumn(columns, idIndex);
      }
      
      columns[columns.length - 1].push(
        'Captured claims represents the available Doctivity data and does not represent 100% of an individual market.',
      );
    }
    return CsvUtil.matrixToString(columns);
  }

  getServicelineIds() {
    const { currentCriteria } = this.props;
    const { patientType } = currentCriteria;
    let serviceline = currentCriteria.serviceline;
    if (serviceline === 'all' || Array.isArray(serviceline) && serviceline.length === 0) {
      serviceline = ServiceLineUtil.getDefault();
    }
    if (patientType === PatientType.CAPTURED_CLAIMS) {
      if (this.getCodesType()) {
        return null;
      }
      if (!Array.isArray(serviceline)) {
        serviceline = [serviceline];
      }
    } else {
      if (Array.isArray(serviceline) && serviceline.length > 0) {
        serviceline = serviceline[0];
      }
    }
    return serviceline;
  }

  getCodes() {
    if (this.props.currentCriteria?.patientType !== PatientType.CAPTURED_CLAIMS) {
      return null;
    }
    const { currentCriteria } = this.props;
    if (currentCriteria.codes) {
      if (Array.isArray(currentCriteria.codes)) {
        return currentCriteria.codes;
      }
      return [currentCriteria.codes];
    }
    return null;
  }

  getCodesType() {
    if (this.props.currentCriteria?.patientType !== PatientType.CAPTURED_CLAIMS) {
      return null;
    }
    return this.props.currentCriteria.codesType;
  }

  getProviderType() {
    return this.props.currentCriteria.claimsType || 'organizations';
  }

  onQueryChange = (query) => {
    this.setState({ query }, this.fetchData);
  };

  generateClaimsCountBar = (claims, index) => {
    const { classes, currentCriteria, providerCounts, organizationCounts } = this.props;
    const { patientType, claimsType } = currentCriteria;

    let patientTypeLabel = 'patients';
    if (patientType === PatientType.CAPTURED_CLAIMS) {
      patientTypeLabel = 'claims';
    }

    // Calculate percentage directly from counts (handles race condition on reload)
    let aggregateClaimsPercentage = claims.aggregateClaimsPercentage;
    if (aggregateClaimsPercentage === null || aggregateClaimsPercentage === undefined) {
      const counts = claimsType === 'organizations' ? organizationCounts : providerCounts;
      const totalMarket = counts?.data?.total;
      if (totalMarket && claims.total) {
        aggregateClaimsPercentage = claims.total / totalMarket;
      }
    }

    return (
      <>
        <span>
          {` ${
            claims.total ? claims.total.toLocaleString() : '<11'
          } ${patientTypeLabel}`}
        </span>
        {aggregateClaimsPercentage !== null &&
        aggregateClaimsPercentage !== undefined ? (
          <span className={classes.aggregatePercentage}>
            {` ~${(aggregateClaimsPercentage * 100).toLocaleString()}% ${
              index === 0 ? 'of Selected Market' : ''
            }`}
          </span>
        ) : (
          <></>
        )}
      </>
    );
  };

  render() {
    const {
      classes,
      providers,
      markets,
      organizations,
      providerCounts,
      organizationCounts,
      servicelines,
      currentCriteria,
      taxonomyCodes 
    } = this.props;
    const {
      claimSetting,
      ready,
      isMarket,
      market: marketId,
      tag: tagId,
      patientType
    } = currentCriteria;
    let market, tag;
    if (!ready) {
      return <LoadingView />;
    }
    if (isMarket) {
      market = this.props.markets?.data?.find((m) => m.id === marketId);
    } else {
      tag = TagsUtil.findTag(tagId, this.props.tagNamespaces);
    }

    const { query } = this.state;

    const claimsType = this.getProviderType();

    let isLoading = false;
    let max = 100;
    let rows = [];
    let data = {};
    let totalMarket = 0;
    let affiliatedPercentage = -1;
    if (claimsType === 'providers') {
      if (
        !providers ||
        !StoreUtil.isLoaded(providers) ||
        !markets ||
        !StoreUtil.isLoaded(markets)
      ) {
        isLoading = true;
      } else {
        data = providers.data;
        rows = providers.data.rows;
        if (providerCounts?.data && providerCounts.data.total >= 0) {
          data = providerCounts.data;
          totalMarket = providerCounts.data.total;
          affiliatedPercentage = (
            (providerCounts.data.total_affiliated / providerCounts.data.total) *
            100
          ).toFixed(2);
        }
      }
    } else if (
      !organizations ||
      !StoreUtil.isLoaded(organizations) ||
      !markets ||
      !StoreUtil.isLoaded(markets)
    ) {
      isLoading = true;
    } else {
      data = organizations.data;
      rows = organizations.data.rows;
      if (organizationCounts?.data && organizationCounts.data.total >= 0) {
        data = organizationCounts.data;
        totalMarket = organizationCounts.data.total;
        affiliatedPercentage = (
          (organizationCounts.data.total_affiliated /
            organizationCounts.data.total) *
          100
        ).toFixed(2);
      }
    }

    if (affiliatedPercentage > -1) {
      affiliatedPercentage = Math.round(affiliatedPercentage * 100) / 100;
    }
    const nonAffiliatedPercentage =
      affiliatedPercentage > -1
        ? Math.round((100 - affiliatedPercentage).toFixed(2) * 100) / 100
        : 0;

    rows.forEach((claims) => {
      if (claims.total === null) {
        claims.total = 0;
      } else {
        claims.total = parseInt(claims.total, 10);
        if (totalMarket) {
          claims.aggregateClaimsPercentage = claims.total / totalMarket;
        }
        if (claimsType === 'providers') {
          if (claims.provider.is_affiliated || claims.provider.clients?.length > 0) {
            claims.affiliated = true;
          }
        } else if (claimsType === 'organizations') {
          if (claims.organization.is_affiliated || claims.organization.affiliated?.length > 0) {
            claims.affiliated = true;
          }
        }

        max = Math.max(max, claims.total);
      }
    });

    let servicelineIds =
      this.getServicelineIds() ?? ServiceLineUtil.getDefault();
    const oldestClaimsDate = new Date(
      latestClaimsDate.getFullYear() - 1,
      latestClaimsDate.getMonth() + 1,
    );
    return (
      <>
        <div className={classes.container}>
          <div className={classes.header}>
            <FormControl
              variant='outlined'
              className={classes.headerDropDown}
              key='filter-type'
            >
              <InputLabel>Type</InputLabel>
              <Select
                value={claimsType}
                onChange={this.onProviderTypeChange}
                label='Type'
              >
                <MenuItem value='providers' key='providers'>
                  Providers
                </MenuItem>
                <MenuItem value='organizations' key='organizations'>
                  Organizations
                </MenuItem>
              </Select>
            </FormControl>
            <ClaimsDateProvider />
          </div>
          <Card>
            <CardContent>
              <TitleTooltip
                title='Market Data'
                tooltip={`Displaying claims analytics from ${oldestClaimsDate.toLocaleString(
                  'default',
                  { year: 'numeric', month: 'long' },
                )} through ${latestClaimsDate.toLocaleString('default', {
                  year: 'numeric',
                  month: 'long',
                })}`}
                onExport={() => this.fetchData(true)}
                onPdfExport={() => this.setState({ showPdfExport: true })}
              />
              <div className={classes.filters}>
                <div className={classes.dropdownsContainer}>
                  {this.props.currentCriteria?.patientType !== PatientType.CAPTURED_CLAIMS && (
                    <FormControl
                      variant='outlined'
                      className={classes.filterDropDown}
                      key='patient-type'
                    >
                      <InputLabel className={classes.filterLabel}>
                        Patients
                      </InputLabel>
                      <Select
                        key='select'
                        value={this.props.currentCriteria?.patientType}
                        onChange={this.onPatientTypeChange}
                        label='Patients'

                      >
                        <MenuItem
                          value={PatientType.ALL_PATIENTS}
                          key='all_patients'
                        >
                          All Projected Patients
                        </MenuItem>
                        <MenuItem
                          value={PatientType.PEDIATRIC_PATIENTS}
                          key='pediatric_patients'
                        >
                          Captured Pediatric Patients
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                  <MarketOrTagSelect
                    filter={{
                      type: (isMarket === false || isMarket === 'false') ? 'tag' : 'market',
                      market,
                      tag,
                    }}
                    onChange={this.onMarketOrTagChange}
                    allowTagSelection={true}
                    markets={this.props.markets}
                    market={this.props.market}
                    serviceLineIds={servicelineIds}
                    currentCriteria={this.props.currentCriteria}
                    type={
                      claimsType === 'providers'
                        ? 'providers'
                        : 'organizations'
                    }
                  />
                  <ServiceLineSelect
                    value={servicelineIds}
                    onChange={this.onServicelineChange}
                    servicelines={servicelines}
                    codes={this.getCodes()}
                    codesType={this.getCodesType()}
                    setFindServiceLine={(findServiceLine) => {
                      this.findServiceLine = findServiceLine;
                    }}
                    multiple={patientType === PatientType.CAPTURED_CLAIMS}
                    //allowCodes={patientType === PatientType.CAPTURED_CLAIMS}
                    showSubServicelines
                    showCustomServiceLines={patientType === PatientType.CAPTURED_CLAIMS}
                  />
                  {patientType !== PatientType.CAPTURED_CLAIMS && (
                    <FormControl
                      variant='outlined'
                      className={classes.filterDropDown}
                      key='filter-setting'
                    >
                      <InputLabel>Care Setting</InputLabel>
                      <Select
                        value={claimSetting ?? 0}
                        onChange={this.onClaimSettingChange}
                        label='Care Setting'
                      >
                        {
                          // eslint-disable-next-line arrow-body-style
                          LocationTypeUtil.getAll()
                            .filter((type) => !type.codesOnly)
                            .map((type) => {
                              return (
                                <MenuItem value={type.id} key={type.id}>
                                  {type.name}
                                </MenuItem>
                              );
                            })
                        }
                      </Select>
                    </FormControl>
                  )}

                  {claimsType === 'providers' && (
                    <TaxonomySelect
                      onChange={this.onTaxonomiesChange}
                      taxonomyCodes={taxonomyCodes} 
                      values={
                        currentCriteria.taxonomies?.length > 0
                          ? currentCriteria.taxonomies
                          : null
                      }
                    />
                  )}
           
                  
                </div>
              </div>
              {affiliatedPercentage > -1 && (
                <div className={classes.affiliatedContainer}>
                  <div className={classes.affiliatedHeader}>
                    <span className={classes.affiliated}>
                      <Icon className={classes.affiliatedIcon}>hub</Icon>
                      <span className={classes.affiliatedPercentage}>
                        ~{affiliatedPercentage}%
                      </span>
                      <span>Affiliated</span>
                    </span>
                    <div className={classes.affiliatedSpacer} />
                    <span className={classes.nonAffiliated}>
                      <span className={classes.affiliatedPercentage}>
                        ~{nonAffiliatedPercentage}%
                      </span>
                      <span>Non-Affiliated</span>
                    </span>
                  </div>
                  <Tooltip
                    placement='bottom-start'
                    title={
                      <Typography className={classes.affiliatedInfoTip}>
                        {patientType === PatientType.ALL_PATIENTS
                          ? 'Projected patients are not unique across any given market, therefore market percentage is an approximation.'
                          : patientType === PatientType.PEDIATRIC_PATIENTS
                          ? 'Captured pediatric patients are not unique across any given market, therefore market percentage is an approximation.'
                          : 'Captured claims represents the available Doctivity data and does not represent 100% of an individual market.'}
                      </Typography>
                    }
                  >
                    <Icon className={classes.affiliatedInfoTipIcon}>
                      info_outlined
                    </Icon>
                  </Tooltip>
                </div>
              )}
              {isLoading && <LoadingView />}
              {!isLoading && this.checkCustomServiceLineProcessing() && (
                <div className={classes.providers}>
                  {this.renderCustomServiceLineProgress()}
                </div>
              )}
              {!isLoading && !this.checkCustomServiceLineProcessing() && (
                <div className={classes.providers}>
                  {claimsType === 'providers' && (
                    <ProviderProjectedPatientsChart patientType={patientType} careSetting={claimSetting} />
                  )}
                  {claimsType === 'organizations' && (
                    <OrgProjectedPatientsChart patientType={patientType} careSetting={claimSetting} />
                  )}
                </div>
              )}
              <div className={classes.pagination}>
                <Paginateable
                  data={data}
                  query={query}
                  onQueryChange={this.onQueryChange}
                />
              </div>
            </CardContent>
          </Card>
        </div>
   <PDFExportDialog
      open={this.state.showPdfExport}
      onClose={() => this.setState({ showPdfExport: false })}
      title={`Market Data - ${
        patientType === PatientType.CAPTURED_CLAIMS 
          ? 'Captured Claims' 
          : patientType === PatientType.PEDIATRIC_PATIENTS 
            ? 'Pediatric Patients' 
            : 'Projected Patients'
      }`}
      reportType={
        patientType === PatientType.CAPTURED_CLAIMS 
          ? 'captured_claims' 
          : 'projected_patients'
      }
      chartData={rows}
      providerCounts={providerCounts?.data}
      totalCount={data?.count || rows.length}
      filters={{
        patientType: this.props.currentCriteria?.patientType,
        market: this.props.market,
        tag: currentCriteria.tag,
        serviceLine: this.findServiceLine?.(servicelineIds),
        careSetting: claimSetting ? LocationTypeUtil.getName(claimSetting) : null,
        specialty:    taxonomyCodes?.data?.rows        
        ?.map(t => t.specialization || t.classification)
        ?.join(', '),
    
        dateRange: `${oldestClaimsDate.toLocaleString('default', { 
          month: 'short', year: 'numeric' 
        })} - ${latestClaimsDate.toLocaleString('default', { 
          month: 'short', year: 'numeric' 
        })}`,
      }}
      defaultOptions={{
        claimsType: claimsType
      }}
    />
      </>
    );
  }

  onPatientTypeChange = (event) => {
    this.props.dispatch(setPatientType(event.target.value.toUpperCase()));
    this.props.updateCriteria({
      patientType: event.target.value.toUpperCase(),
    });
  };

  onMarketOrTagChange = (filter) => {
    const { market, tag } = this.props.currentCriteria;
    this.props.updateCriteria({
      isMarket: filter.type === 'market',
      market: (filter.type === 'market' ? filter.market : market)?.id,
      tag: (filter.type === 'tag' ? filter.tag : tag)?.id,
    });
  };

  onServicelineChange = (ids, idTypes) => {
    if (idTypes === 'dx' || idTypes === 'px') {
      this.props.updateCriteria({
        codes: ids,
        codesType: idTypes,
        serviceline: null,
      });
    } else {
      this.props.updateCriteria({
        serviceline: ids,
        codes: null,
        codesType: null,
      });
    }
  };

  onProviderTypeChange = (e) => {
    const params = new URLSearchParams(this.props.router.location.search);
    const report = params.get('report');
    const providerType = e.target.value;
    this.props.dispatch(setProviderType(providerType));
    this.props.updateCriteria({
      claimsType: providerType,
      report: report
    });
  };

  onTaxonomiesChange = (selectedTaxonomies) => {
    console.log('ClaimsPage.onTaxonomiesChange received:', selectedTaxonomies);
    const taxonomies = selectedTaxonomies && selectedTaxonomies.length > 0 
      ? selectedTaxonomies.map((taxonomy) => taxonomy.code)
      : null;
    console.log('Setting taxonomies to:', taxonomies);
    this.props.updateCriteria({ taxonomies });
  };

  onClaimSettingChange = (event) => {
    this.props.updateCriteria({
      claimSetting: event.target.value,
    });
  };

}

ClaimsPage.propTypes = {
  classes: PropTypes.object,
  router: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  providers: PropTypes.object.isRequired,
  providerCounts: PropTypes.object,
  query: PropTypes.object.isRequired,
  servicelineId: PropTypes.number,
  providerType: PropTypes.string,
  market: PropTypes.object,
  markets: PropTypes.object,
  organizations: PropTypes.object,
  organizationCounts: PropTypes.object,
  patientType: PropTypes.oneOf(PatientTypes),
  clientId: PropTypes.number,
  servicelines: PropTypes.array,
  user: PropTypes.object,
  updateCriteria: PropTypes.func.isRequired,
  currentCriteria: PropTypes.object.isRequired,
  getCriteria: PropTypes.func.isRequired,
  tagNamespaces: PropTypes.object,
  taxonomyCodes: PropTypes.object.isRequired,  
};

function mapStateToProps(state) {
  const { taxonomyCodes } = state;

  return {
    providers: StoreUtil.get(state.analytics, 'CLAIMS_PER_PROVIDER'),
    organizations: StoreUtil.get(state.analytics, 'CLAIMS_PER_ORGANIZATION'),
    providerCounts: StoreUtil.get(
      state.analytics,
      'CLAIMS_PER_PROVIDER_COUNTS',
    ),
    organizationCounts: StoreUtil.get(
      state.analytics,
      'CLAIMS_PER_ORGANIZATION_COUNTS',
    ),
    market: MarketsUtil.getSelectedMarket(state),
    markets: MarketsUtil.getMarkets(state),
    servicelines: state.claims && state.claims.servicelines_grouped,
    patientType: state.app.patientType,
    clientId: state.app.selectedClient,
    providerType: state.app.providerType,
    user: state.user,
    tagNamespaces: StoreUtil.get(
      state.tagNamespaces,
      'API_TAG_NAMESPACES_LIST_BY_CLIENT',
    ),
    taxonomyCodes: StoreUtil.get(taxonomyCodes, StoreUtil.COMMON_TABLE)

  };
}

const styled = withStyles(styles)(withReportCriteria('claims', ClaimsPage));
const connected = connect(mapStateToProps)(styled);
export { connected as ClaimsPage };